/**
 * /admin/schools/:schoolId — 学校详情四标签(spec-school-student-admin §5.1)。
 * 概览统计卡可点击下钻到对应标签;健康度告警有则显示;学生双区
 * (在读/归档)+ 班级/状态过滤 + 搜索(300ms 防抖)+ 服务端分页。
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/OidcProvider";

import {
  authHeaders,
  useSchoolClasses,
  useSchoolOverview,
  useSchoolStudentsAdmin,
  useSchoolTeachers,
  useSchools,
} from "@/api/school";
import { CreateClassModal } from "../classes/CreateClassModal";
import { ClassEditForm } from "../classes/ClassEditForm";
import { STATUS_BADGE } from "../students/LifecyclePanel";
import { TransferWizard } from "./TransferWizard";
import { TeacherSubjectsCell, TeacherSubjectsDialog } from "./TeacherSubjectsDialog";
import { TeacherTransferDialog } from "./TeacherTransferDialog";
import { TeacherInsightDialog } from "./TeacherInsightDialog";
import type { SchoolStudentRow, SchoolTeacherRow } from "@/api/school";

type Tab = "overview" | "classes" | "teachers" | "students";

export default function SchoolDetailPage() {
  const { t } = useTranslation();
  const { schoolId } = useParams<{ schoolId: string }>();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) ?? "overview";
  const schools = useSchools();
  const school = schools.data?.find((s) => s.id === schoolId);

  function setTab(t2: Tab, extra?: Record<string, string>) {
    const next = new URLSearchParams();
    next.set("tab", t2);
    for (const [k, v] of Object.entries(extra ?? {})) next.set(k, v);
    setParams(next);
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-5">
        <Link to="/admin/schools" className="text-xs text-sky-600 hover:underline">
          ← {t("schoolDetail.back")}
        </Link>
        <div className="mt-1 flex items-center gap-3">
          {school?.logo ? (
            <img src={school.logo} alt="" className="h-10 w-10 rounded object-contain" />
          ) : (
            <span className="text-2xl">🏫</span>
          )}
          <h1 className="text-2xl font-semibold text-slate-800">{school?.name ?? "…"}</h1>
        </div>
      </header>

      <nav className="mb-5 flex gap-1 rounded-lg border border-slate-200 bg-white p-1 w-fit">
        {(["overview", "classes", "teachers", "students"] as Tab[]).map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-md text-sm ${
              tab === k ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t(`schoolDetail.tabs.${k}`)}
          </button>
        ))}
      </nav>

      {tab === "overview" && <OverviewTab schoolId={schoolId!} onDrill={setTab} />}
      {tab === "classes" && <ClassesTab schoolId={schoolId!} />}
      {tab === "teachers" && <TeachersTab schoolId={schoolId!} />}
      {tab === "students" && <StudentsTab schoolId={schoolId!} />}
    </div>
  );
}

function KnowledgeAssetsCard({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const q = useQueryKA(schoolId);
  if (!q.data) return null;
  const d = q.data;
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">{t("assets.title")}</h3>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-2xl font-semibold text-slate-800">{d.month_ingests}</div>
          <div className="text-xs text-slate-500">{t("assets.monthIngests")}</div>
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-800">{d.approved_total}</div>
          <div className="text-xs text-slate-500">{t("assets.approvedTotal")}</div>
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-300">{d.rag_hits ?? "—"}</div>
          <div className="text-xs text-slate-400">{t("assets.ragHits")}</div>
        </div>
      </div>
    </div>
  );
}

function useQueryKA(schoolId: string) {
  return useQuery({
    queryKey: ["admin", "school", schoolId, "knowledge-assets"],
    queryFn: async () => {
      const r = await fetch(`/api/admin/schools/${schoolId}/knowledge-assets`, {
        credentials: "include", headers: authHeaders(),
      });
      if (!r.ok) throw new Error(String(r.status));
      return r.json() as Promise<{ month_ingests: number; approved_total: number;
        month_contributors: number; rag_hits: number | null }>;
    },
  });
}

function OverviewTab({
  schoolId,
  onDrill,
}: {
  schoolId: string;
  onDrill: (t: Tab, extra?: Record<string, string>) => void;
}) {
  const { t } = useTranslation();
  const ov = useSchoolOverview(schoolId);
  if (ov.isLoading) return <p className="text-sm text-slate-400">…</p>;
  if (!ov.data) return null;
  const c = ov.data.counts;
  const a = ov.data.alerts;
  const cards: { key: string; value: number; tab: Tab; extra?: Record<string, string>; warn?: boolean }[] = [
    { key: "classes", value: c.classes, tab: "classes" },
    { key: "teachers", value: c.teachers, tab: "teachers" },
    { key: "activeStudents", value: c.active_students, tab: "students" },
    { key: "onLeave", value: c.on_leave, tab: "students", extra: { status: "on_leave" } },
    { key: "archivedStudents", value: c.archived_students, tab: "students", extra: { archived: "1" } },
    { key: "teacherlessClasses", value: c.teacherless_classes, tab: "classes", warn: c.teacherless_classes > 0 },
  ];
  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-5">
        {cards.map((card) => (
          <button
            key={card.key}
            onClick={() => onDrill(card.tab, card.extra)}
            className={`rounded-xl border bg-white p-3 text-left hover:shadow-sm ${
              card.warn ? "border-rose-300" : "border-slate-200"
            }`}
          >
            <div className={`text-2xl font-semibold ${card.warn ? "text-rose-600" : "text-slate-800"}`}>
              {card.value}
              {card.warn && " ⚠️"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{t(`schoolDetail.cards.${card.key}`)}</div>
          </button>
        ))}
      </div>

      {(a.teacherless_classes.length > 0 || a.unassigned_active > 0 || a.overdue_on_leave.length > 0) && (
        <div className="space-y-2">
          {a.teacherless_classes.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800">
              🔴 {t("schoolDetail.alerts.teacherless", { n: a.teacherless_classes.length })}:{" "}
              {a.teacherless_classes.map((x) => x.name).join("、")}
            </div>
          )}
          {a.unassigned_active > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              🟡 {t("schoolDetail.alerts.unassigned", { n: a.unassigned_active })}
            </div>
          )}
          {a.overdue_on_leave.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
              🟡 {t("schoolDetail.alerts.overdue", { n: a.overdue_on_leave.length })}:{" "}
              {a.overdue_on_leave.map((x) => `${x.name_zh}(${x.expected_return_date})`).join("、")}
            </div>
          )}
        </div>
      )}
      <KnowledgeAssetsCard schoolId={schoolId} />
    </div>
  );
}

function ClassesTab({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const rows = useSchoolClasses(schoolId);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  return (
    <div>
    <div className="mb-3 flex justify-end">
      <button
        type="button"
        onClick={() => setShowCreate(true)}
        className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600"
      >
        {t("schoolDetail.newClass")}
      </button>
    </div>
    {showCreate && (
      <CreateClassModal
        schoolId={schoolId}
        onClose={() => {
          setShowCreate(false);
          void rows.refetch();
        }}
      />
    )}
    <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.classCols.name")}</th>
            <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.classCols.grade")}</th>
            <th className="text-right px-4 py-2 font-medium">{t("schoolDetail.classCols.students")}</th>
            <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.classCols.teachers")}</th>
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {(rows.data ?? []).map((c) =>
            editingId === c.id ? (
              <tr key={c.id} className="border-t border-slate-100 bg-sky-50/40">
                <td colSpan={5} className="px-4 py-3">
                  <ClassEditForm
                    classId={c.id}
                    initial={{ name: c.name, grade: c.grade }}
                    onDone={() => setEditingId(null)}
                  />
                </td>
              </tr>
            ) : (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <Link to={`/classes/${c.id}`} className="text-sky-700 hover:underline">
                    {c.name}
                  </Link>
                </td>
                <td className="px-4 py-2.5 text-slate-600">{c.grade}</td>
                <td className="px-4 py-2.5 text-right font-mono">{c.student_count}</td>
                <td className="px-4 py-2.5 text-slate-600">
                  {c.teachers.length > 0 ? (
                    c.teachers.join("、")
                  ) : (
                    <span className="text-rose-500">{t("schoolDetail.noTeacher")}</span>
                  )}
                </td>
                <td className="px-2 py-2.5 text-right">
                  {/* 改名/改年级(2026-09-13):园长在这里也能直接改,不必进班级页。 */}
                  <button
                    type="button"
                    onClick={() => setEditingId(c.id)}
                    title={t("classes.edit.button")}
                    aria-label={t("classes.edit.button")}
                    className="text-slate-400 hover:text-sky-600"
                  >
                    ✎
                  </button>
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
    </div>
  );
}

/** 「角色」列 —— 集团管理员在这里把成员在老师/园长之间切换。
 *
 *  这个控件之前不存在,园长只能靠人工连数据库 UPDATE 产生,于是「园长自己
 *  就能管本校」落不了地。后端 PUT /api/admin/teachers/:id/role **仅集团
 *  管理员**可调 —— 园长任命园长是校内提权。
 *
 *  园长自 2026-09-02 起也进得来这个页面(ManagerRoute),所以这里对非管理员
 *  降级成只读徽标:给一个点了必然 403 的下拉,比不给更糟。 */
function TeacherRoleCell({ schoolId, row }: { schoolId: string; row: SchoolTeacherRow }) {
  const { t } = useTranslation();
  const { state } = useAuth();
  const isAdmin = state.status === "authenticated" && state.user.isAdmin;
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function change(role: string) {
    if (role === row.role) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(`/api/admin/teachers/${row.nova_user_id}/role`, {
        method: "PUT",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      if (!r.ok) {
        // 后端的 detail 是给人看的中文(例如「还没有本校的教师档案」)。
        // 只抛 HTTP 码等于把它扔了 —— 分配学科那个坑就是这么来的。
        const b = (await r.json().catch(() => ({}))) as { detail?: string };
        throw new Error(b.detail ?? `HTTP ${r.status}`);
      }
      await qc.invalidateQueries({ queryKey: ["admin", "school", schoolId, "teachers"] });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!isAdmin) {
    return (
      <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${
        row.role === "admin" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"
      }`}>
        {t(`schoolDetail.roles.${row.role}`, row.role)}
      </span>
    );
  }

  return (
    <div>
      <select
        value={row.role}
        disabled={busy}
        onChange={(e) => void change(e.target.value)}
        className={`rounded border px-2 py-0.5 text-xs whitespace-nowrap disabled:opacity-50 ${
          row.role === "admin"
            ? "border-violet-200 bg-violet-50 text-violet-700"
            : "border-slate-200 bg-white text-slate-600"
        }`}
      >
        <option value="teacher">{t("schoolDetail.roles.teacher", "老师")}</option>
        <option value="admin">{t("schoolDetail.roles.admin", "校长")}</option>
      </select>
      {err && <p className="mt-1 max-w-[16rem] text-xs text-rose-600">{err}</p>}
    </div>
  );
}

function TeachersTab({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const rows = useSchoolTeachers(schoolId);
  const [moving, setMoving] = useState<{ id: string; name: string; classes: string[] } | null>(null);
  const [insightFor, setInsightFor] = useState<{ nova_user_id: string; name: string } | null>(null);
  const [subjectsFor, setSubjectsFor] = useState<{ nova_user_id: string; name: string } | null>(null);
  return (
    <div>
      {/* Jarod 2026-08-04:在院所管理里邀请老师/园长 */}
      <div className="mb-3 flex justify-end">
        <Link
          to="/admin/invitations"
          className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600"
        >
          {t("schoolDetail.inviteTeacher")}
        </Link>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.teacherCols.name")}</th>
            <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.teacherCols.email")}</th>
            <th className="text-left px-4 py-2 font-medium whitespace-nowrap">{t("schoolDetail.teacherCols.role")}</th>
            <th className="text-left px-4 py-2 font-medium whitespace-nowrap">{t("schoolDetail.teacherCols.classes")}</th>
            <th className="text-left px-4 py-2 font-medium whitespace-nowrap">{t("schoolDetail.teacherCols.subjects")}</th>
            <th className="px-4 py-2 w-20"></th>
          </tr>
        </thead>
        <tbody>
          {(rows.data ?? []).map((tr) => (
            <tr key={tr.id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-4 py-2.5 text-slate-800">{tr.name}</td>
              <td className="px-4 py-2.5 text-slate-500">{tr.email ?? "—"}</td>
              <td className="px-4 py-2.5 whitespace-nowrap">
                <TeacherRoleCell schoolId={schoolId} row={tr} />
              </td>
              <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                {tr.classes.length > 0 ? tr.classes.join("、") : "—"}
              </td>
              <td className="px-4 py-2.5">
                <TeacherSubjectsCell
                  novaUserId={(tr as { nova_user_id?: string }).nova_user_id ?? tr.id}
                  onEdit={() => setSubjectsFor({ nova_user_id: (tr as { nova_user_id?: string }).nova_user_id ?? tr.id, name: tr.name })}
                />
              </td>
              <td className="px-4 py-2.5 text-right space-x-1 whitespace-nowrap">
                <button
                  onClick={() => setInsightFor({ nova_user_id: (tr as { nova_user_id?: string }).nova_user_id ?? tr.id, name: tr.name })}
                  className="px-2 py-1 text-xs rounded border border-violet-200 text-violet-600 hover:bg-violet-50 whitespace-nowrap"
                >
                  {t("insights.entry")}
                </button>
                <button
                  onClick={() => setMoving({ id: tr.id, name: tr.name, classes: tr.classes })}
                  className="px-2 py-1 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-50 whitespace-nowrap"
                >
                  {t("teacherTransfer.entry")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {moving && (
        <TeacherTransferDialog
          fromSchoolId={schoolId}
          teacher={moving}
          onClose={() => setMoving(null)}
        />
      )}
      {subjectsFor && (
        <TeacherSubjectsDialog
          novaUserId={subjectsFor.nova_user_id}
          teacherName={subjectsFor.name}
          onClose={() => setSubjectsFor(null)}
        />
      )}
      {insightFor && (
        <TeacherInsightDialog teacher={insightFor} onClose={() => setInsightFor(null)} />
      )}
    </div>
  );
}

function StudentsTab({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [archived, setArchived] = useState(params.get("archived") === "1");
  const [statusF, setStatusF] = useState(params.get("status") ?? "");
  const [classF, setClassF] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const classes = useSchoolClasses(schoolId);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(h);
  }, [q]);
  useEffect(() => setPage(1), [archived, statusF, classF, debouncedQ]);

  const data = useSchoolStudentsAdmin(schoolId, {
    archived,
    status: statusF || undefined,
    class_id: classF || undefined,
    q: debouncedQ || undefined,
    page,
  });

  const totalPages = data.data ? Math.max(1, Math.ceil(data.data.total / data.data.per_page)) : 1;

  // 多选:Map 保对象引用,跨页保持选择 (§5.1)
  const [picked, setPicked] = useState<Map<string, SchoolStudentRow>>(new Map());
  const [wizardOpen, setWizardOpen] = useState(false);
  const pageRows = data.data?.students ?? [];
  const allPagePicked = pageRows.length > 0 && pageRows.every((s) => picked.has(s.id));
  function togglePick(s: SchoolStudentRow) {
    setPicked((m) => {
      const next = new Map(m);
      if (next.has(s.id)) next.delete(s.id); else next.set(s.id, s);
      return next;
    });
  }
  function togglePage() {
    setPicked((m) => {
      const next = new Map(m);
      if (allPagePicked) pageRows.forEach((s) => next.delete(s.id));
      else pageRows.forEach((s) => next.set(s.id, s));
      return next;
    });
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
          {[false, true].map((z) => (
            <button
              key={String(z)}
              onClick={() => { setArchived(z); setStatusF(""); }}
              className={`px-3 py-1.5 ${
                archived === z ? "bg-sky-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {z ? t("schoolDetail.zoneArchived") : t("schoolDetail.zoneActive")}
            </button>
          ))}
        </div>
        <select
          value={statusF}
          onChange={(e) => setStatusF(e.target.value)}
          className="rounded border border-slate-200 px-2 py-1.5 text-xs"
        >
          <option value="">{t("schoolDetail.allStatuses")}</option>
          {(archived ? ["graduated", "transferred_out", "withdrawn"] : ["active", "on_leave"]).map((st) => (
            <option key={st} value={st}>{STATUS_BADGE[st]?.label ?? st}</option>
          ))}
        </select>
        <select
          value={classF}
          onChange={(e) => setClassF(e.target.value)}
          className="rounded border border-slate-200 px-2 py-1.5 text-xs"
        >
          <option value="">{t("schoolDetail.allClasses")}</option>
          {(classes.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("schoolDetail.searchPlaceholder")}
          className="rounded border border-slate-200 px-2 py-1.5 text-xs w-52"
        />
        <span className="ml-auto text-xs text-slate-400">
          {t("schoolDetail.total", { n: data.data?.total ?? 0 })}
        </span>
        {!archived && picked.size > 0 && (
          <button
            onClick={() => setWizardOpen(true)}
            className="px-3 py-1.5 rounded bg-violet-600 text-white text-xs font-medium hover:bg-violet-700"
          >
            {t("transfer.entry", { n: picked.size })}
          </button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {!archived && (
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={allPagePicked} onChange={togglePage} />
                </th>
              )}
              <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.studentCols.name")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.studentCols.code")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.studentCols.grade")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.studentCols.class")}</th>
              <th className="text-left px-4 py-2 font-medium">{t("schoolDetail.studentCols.status")}</th>
            </tr>
          </thead>
          <tbody>
            {(data.data?.students ?? []).map((s) => {
              const onLeave = s.status === "on_leave";
              return (
                <tr key={s.id} className={`border-t border-slate-100 hover:bg-slate-50 ${onLeave ? "opacity-50" : ""}`}>
                  {!archived && (
                    <td className="px-3 py-2.5">
                      <input type="checkbox" checked={picked.has(s.id)} onChange={() => togglePick(s)} />
                    </td>
                  )}
                  <td className="px-4 py-2.5">
                    <Link to={`/students/${s.id}`} className="text-sky-700 hover:underline">
                      {s.name_zh}
                    </Link>
                    {s.name_en && <span className="ml-1 text-xs text-slate-400">({s.name_en})</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{s.student_code ?? "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600">{s.grade}</td>
                  <td className="px-4 py-2.5 text-slate-600">{s.class_name ?? "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-xs ${STATUS_BADGE[s.status]?.cls ?? "bg-slate-100"}`}>
                      {STATUS_BADGE[s.status]?.label ?? s.status}
                    </span>
                    {onLeave && s.expected_return_date && (
                      <span className="ml-1.5 text-[11px] text-amber-600">
                        {t("lifecycle.expectedReturn", { date: s.expected_return_date })}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {wizardOpen && (
        <TransferWizard
          fromSchoolId={schoolId}
          students={Array.from(picked.values())}
          onClose={() => setWizardOpen(false)}
          onDone={() => setPicked(new Map())}
        />
      )}

      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40"
          >
            ←
          </button>
          <span className="text-slate-500">{page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}
